<script>

import MerchantPromotion from '@/model/dataLayer/api/MembershipSystem/MerchantPromotion'
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import { saveFile, showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import dayjs from 'dayjs'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import { getJoinedBrandName } from '@/model/dataLayer/api/Vip/BusinessLayer'
import GlobalConfig from '@/model/global/GlobalSettings'
import JoinedBrandNotice from '@/views/AadenPages/memberShipReborn/Common/JoinedBrandNotice.vue'

export default {
  name: 'MerchantPromotion',
  components: {
    JoinedBrandNotice,
    LoadingProvider,
    PageSubHeader,
    PageHeader,
    EnhancedImageUploader
  },
  data () {
    return {
      saveLoading: false,
      // 已加入品牌 → 品牌名：推广改由品牌在会员后台发，这里不再管理
      brandName: '',
      promotionList: [],
      editObject: {
        imageUrl: '',
        title: '',
        contentMD: '',
        longImageUrl: '',
        active: false,
        clickCount: 0,
        viewCount: 0,
        startDate: null,
        endDate: null,
        createTimestamp: null,
        ctr: null
      },
      newMerchantPromotionDialog: false,
      model: MerchantPromotion,
      loading: false,
      formValid: true,
      startDateMenu: false,
      endDateMenu: false,
      rules: {
        required: v => !!v || '此字段为必填项'
      }
    }
  },
  async mounted () {
    this.loading = true
    this.brandName = await getJoinedBrandName(GlobalConfig.blId)
    if (this.brandName) {
      this.loading = false
      return
    }
    await this.reload()
  },
  methods: {
    async deletePromotion (id) {
      await this.model.remove(id)
      await this.reload()
    },
    async reload () {
      this.loading = true
      this.promotionList = await this.model.getList(true)
      this.loading = false
    },
    showNewDialog () {
      // Reset editObject for new promotion
      this.editObject = {
        imageUrl: '',
        title: '',
        contentMD: '',
        longImageUrl: '',
        active: false,
        clickCount: 0,
        viewCount: 0,
        startDate: null,
        endDate: null,
        createTimestamp: null,
        ctr: null
      }
      this.newMerchantPromotionDialog = true
    },
    editPromotion (promotion) {
      // Copy promotion data to editObject
      this.editObject = { ...promotion }
      this.editObject.startDate = promotion.startDate ? dayjs(promotion.startDate).format('YYYY-MM-DD') : null
      this.editObject.endDate = promotion.endDate ? dayjs(promotion.endDate).format('YYYY-MM-DD') : null
      this.newMerchantPromotionDialog = true
    },
    validateForm () {
      return this.$refs.form.validate()
    },
    async savePromotion () {
      if (!this.validateForm()) {
        return
      }

      this.saveLoading = true
      try {
        // Process image uploads if they're File objects
        if (this.editObject.imageUrl && typeof this.editObject.imageUrl !== 'string') {
          this.editObject.imageUrl = await saveFile(this.editObject.imageUrl)
        }

        if (this.editObject.longImageUrl && typeof this.editObject.longImageUrl !== 'string') {
          this.editObject.longImageUrl = await saveFile(this.editObject.longImageUrl)
        }

        // Format dates if they exist
        if (this.editObject.startDate) {
          this.editObject.startDate = dayjs(this.editObject.startDate).format('YYYY-MM-DD') + 'T00:00:00'
        }

        if (this.editObject.endDate) {
          this.editObject.endDate = dayjs(this.editObject.endDate).format('YYYY-MM-DD') + 'T00:00:00'
        }

        // Check if this is a new promotion or an edit
        if (this.editObject.id) {
          // Update existing promotion
          await this.model.edit(this.editObject)
          showSuccessMessage('推广更新成功！')
        } else {
          // Set creation timestamp for new promotions
          this.editObject.createTimestamp = new Date().toISOString()

          // Save the new promotion
          await this.model.add(this.editObject)
          showSuccessMessage('推广创建成功！')
        }

        this.newMerchantPromotionDialog = false

        // Reload the promotion list
        await this.reload()

        // Reset form
        this.editObject = {
          imageUrl: '',
          title: '',
          contentMD: '',
          longImageUrl: '',
          active: false,
          clickCount: 0,
          viewCount: 0,
          startDate: null,
          endDate: null,
          createTimestamp: null,
          ctr: null
        }

        this.$refs.form.resetValidation()
      } catch (error) {
        console.error('Error saving promotion:', error)
        showErrorMessage('保存失败，请重试')
      } finally {
        this.saveLoading = false
      }
    }
  }
}
</script>

<template>
  <v-container v-if="brandName">
    <joined-brand-notice :brand-name="brandName">
      商家推广由品牌在会员后台「营销 → 商家推广」统一发布，会员 APP 首页「最新动态」显示的是品牌的推广。
    </joined-brand-notice>
  </v-container>
  <v-container v-else>
    <div class="d-flex align-center justify-center mb-6">
      <page-header>商家推广</page-header>
      <v-spacer />
      <v-btn
        class="primary"
        @click="showNewDialog"
      >
        新增
      </v-btn>
    </div>

    <!-- Promotion Cards Grid -->
    <loading-provider :loading="loading">
      <v-row>
        <v-col
          v-for="(promotion, index) in promotionList"
          :key="promotion.id || index"
          cols="12"
          sm="6"
        >
          <v-card
            class="promotion-card"
            elevation="0"
            hover
            outlined
            @click="editPromotion(promotion)"
          >
            <!-- Promotion Image -->
            <v-img
              :src="promotion.imageUrl"
              aspect-ratio="1.7778"
              class="promotion-image"
              width="100%"
            >
              <template #placeholder>
                <v-row
                  align="center"
                  class="fill-height ma-0"
                  justify="center"
                >
                  <v-progress-circular
                    color="grey lighten-5"
                    indeterminate
                  />
                </v-row>
              </template>
            </v-img>

            <!-- Promotion Details -->
            <div class="pa-4">
                <div class="d-flex align-center">
                  <h3 class="text-truncate promotion-title">
                    {{ promotion.title }}
                  </h3>
                  <v-chip
                    :color="promotion.active ? 'success' : 'grey'"
                    class="ml-2"
                    label
                    small
                  >
                    {{ promotion.active ? '激活' : '未激活' }}
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    icon
                    small
                    @click.stop="deletePromotion(promotion.id)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>

                <div class="mt-2 text-caption grey--text">
                  <div>
                    开始日期: {{ promotion.startDate ? promotion.startDate : '暂未设置' }}
                  </div>
                  <div>
                    结束日期: {{ promotion.endDate ? promotion.endDate : '暂未设置' }}
                  </div>
                  <div class="mt-1">
                    浏览: {{ promotion.viewCount || 0 }} | 点击: {{ promotion.clickCount || 0 }}
                  </div>
                </div>
            </div>
          </v-card>
        </v-col>

        <!-- Empty State -->
        <v-col
          v-if="promotionList.length === 0"
          cols="12"
        >
          <v-card
            class="pa-6 text-center"
            outlined
          >
            <v-icon
              color="grey lighten-1"
              size="64"
            >
              mdi-information-outline
            </v-icon>
            <div class="text-h6 mt-2 grey--text">
              暂无推广内容
            </div>
            <div class="mt-2">
              点击"新增"按钮创建第一个推广
            </div>
          </v-card>
        </v-col>
      </v-row>
    </loading-provider>

    <v-dialog
      v-model="newMerchantPromotionDialog"
      max-width="800"
    >
      <v-card class="pa-4">
        <page-sub-header>新的推广</page-sub-header>

        <v-form
          ref="form"
          v-model="formValid"
          lazy-validation
        >
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editObject.title"
                  :rules="[rules.required]"
                  dense
                  label="标题"
                  outlined
                  placeholder="输入推广标题"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <div class="mb-2">
                  <div class="font-weight-bold">
                    推广图片<span>*</span>
                  </div>
                  <div class="text-body-2">
                    上传推广图片 (JPG, PNG)
                  </div>
                </div>
                <enhanced-image-uploader
                  v-model="editObject.imageUrl"
                  :max-size-m-b="2"
                  hint="上传推广图片 (JPG, PNG)"
                />
              </v-col>

              <v-col cols="6">
                <div class="mb-2">
                  <div class="font-weight-bold">
                    长图片
                  </div>
                  <div class="text-body-2">
                    上传推广长图片 (JPG, PNG)
                  </div>
                </div>
                <enhanced-image-uploader
                  v-model="editObject.longImageUrl"
                  :max-size-m-b="2"
                  hint="上传推广长图片 (JPG, PNG)"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <div class="mb-2">
                  <div class="font-weight-bold">
                    内容描述<span>*</span>
                  </div>
                  <div class="text-body-2">
                    推广的详细描述
                  </div>
                </div>
                <MarkdownEditor
                  v-model="editObject.contentMD"
                  :rules="[rules.required]"
                  height="300px"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  max-width="290px"
                  min-width="290px"
                  offset-y
                  transition="scale-transition"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="editObject.startDate"
                      dense
                      label="开始日期"
                      outlined
                      placeholder="选择开始日期"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editObject.startDate"
                    no-title
                    scrollable
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  max-width="290px"
                  min-width="290px"
                  offset-y
                  transition="scale-transition"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="editObject.endDate"
                      dense
                      label="结束日期"
                      outlined
                      placeholder="选择结束日期"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editObject.endDate"
                    no-title
                    scrollable
                    @input="endDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-switch
                  v-model="editObject.active"
                  color="primary"
                  label="激活推广"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="newMerchantPromotionDialog = false"
            >
              取消
            </v-btn>
            <v-btn
              :disabled="saveLoading || !formValid"
              :loading="saveLoading"
              color="primary"
              @click="savePromotion"
            >
              保存
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.promotion-card {
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  overflow: hidden;
}

.promotion-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
}

.promotion-image {
  background-color: #f5f5f5;
}

.promotion-title {
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .promotion-title {
    max-width: 60%;
  }
}
</style>
